import { Flex } from "antd";
import React from "react";

import { PageBlock } from "@/shared/pageComposition/BlockList";
import BaseButton from "@/shared/ui/BaseButton";
import { BlockLoader } from "@/shared/ui/BlockLoader";

import { CheckoutList } from "./components/CheckoutList";
import {
    CheckoutQueueBlockedInfo,
    CheckoutQueueSyncingInfo,
} from "./components/CheckoutQueueInfo";
import { ConfirmOrderModal } from "./modals/ConfirmOrderModal";

export type CheckoutModel = {
    queue: {
        isQueueBlocked: boolean;
        isQueueSyncing: boolean;
        uniqueOrdersCount: number;
    };
    checkout: {
        isCheckoutAvailable: boolean;
        resetCheckoutsLoading: boolean;
        isProcessingClosingOrder: boolean;
        checkedOrderIds: Set<string>;
        isAnyOrderChosen: boolean;
        offlineMode: boolean;
        onToggleOrderId: (orderId: string) => void;
        onResetCheckouts: () => Promise<void>;
        onOpenConfirmModal: () => void;
    };
    modal: {
        isConfirmModalOpened: boolean;
        isProcessingClosingOrder: boolean;
        isAnyOrderChosen: boolean;
        onClose: () => void;
        onConfirm: () => void;
    };
};

const CheckoutQueueStatusBlock: React.FC<{
    isQueueBlocked: boolean;
    isQueueSyncing: boolean;
    uniqueOrdersCount: number;
}> = ({ isQueueBlocked, isQueueSyncing, uniqueOrdersCount }) => {
    if (isQueueBlocked) return <CheckoutQueueBlockedInfo />;

    if (isQueueSyncing) {
        return <CheckoutQueueSyncingInfo count={uniqueOrdersCount} />;
    }

    return null;
};

const CheckoutContentBlock: React.FC<CheckoutModel["checkout"]> = ({
    isCheckoutAvailable,
    resetCheckoutsLoading,
    isProcessingClosingOrder,
    checkedOrderIds,
    isAnyOrderChosen,
    offlineMode,
    onToggleOrderId,
    onResetCheckouts,
    onOpenConfirmModal,
}) => {
    if (!isCheckoutAvailable) return null;

    if (resetCheckoutsLoading || isProcessingClosingOrder) {
        return (
            <Flex vertical flex={1} align="center" justify="center">
                <BlockLoader />
            </Flex>
        );
    }

    return (
        <>
            <CheckoutList
                checkedOrderIds={checkedOrderIds}
                onToggleOrderId={onToggleOrderId}
                onReset={onResetCheckouts}
            />

            {!offlineMode && isAnyOrderChosen && (
                <BaseButton
                    onClick={onOpenConfirmModal}
                    type="primary"
                    disabled={!isAnyOrderChosen}
                >
                    Оформити замовлення
                </BaseButton>
            )}
        </>
    );
};

const ConfirmOrderModalBlock: React.FC<CheckoutModel["modal"]> = ({
    isConfirmModalOpened,
    isProcessingClosingOrder,
    isAnyOrderChosen,
    onClose,
    onConfirm,
}) => (
    <ConfirmOrderModal
        open={isConfirmModalOpened}
        loading={isProcessingClosingOrder}
        disabled={!isAnyOrderChosen}
        onClose={onClose}
        onConfirm={onConfirm}
    />
);

export const getCheckoutBlocks = (
    model: CheckoutModel,
): PageBlock<CheckoutModel>[] => [
    {
        key: "queue-status",
        order: 10,
        isVisible: () => true,
        render: () => (
            <CheckoutQueueStatusBlock
                isQueueBlocked={model.queue.isQueueBlocked}
                isQueueSyncing={model.queue.isQueueSyncing}
                uniqueOrdersCount={model.queue.uniqueOrdersCount}
            />
        ),
    },
    {
        key: "checkout-content",
        order: 20,
        isVisible: (model) => model.checkout.isCheckoutAvailable,
        render: () => <CheckoutContentBlock {...model.checkout} />,
    },
    {
        key: "confirm-modal",
        order: 30,
        isVisible: () => true,
        render: () => <ConfirmOrderModalBlock {...model.modal} />,
    },
];
